import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import BrandingSettings from '../components/settings/BrandingSettings';
import ProductionSettings from '../components/ProductionSettings';
import CompanyEmailSettings from '../components/CompanyEmailSettings';
import { 
  Building2, 
  MapPin, 
  Users, 
  Plus, 
  Pencil, 
  Trash2,
  Loader2,
  Save,
  Cloud,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Info,
  ExternalLink,
  UserPlus,
  Key,
  Eye,
  EyeOff,
  Palette,
  Briefcase,
  Factory,
  Mail,
  Search
} from 'lucide-react';

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [sites, setSites] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  
  // Plan limits
  const [planLimits, setPlanLimits] = useState({ maxUsers: 999, maxEmployees: 999, isPro: true });
  
  // Get tab from URL or default to 'departments'
  const activeTab = searchParams.get('tab') || 'departments';
  
  const handleTabChange = (value) => {
    setSearchParams({ tab: value });
  };
  
  // Dialogs
  const [deptDialog, setDeptDialog] = useState({ open: false, dept: null });
  const [siteDialog, setSiteDialog] = useState({ open: false, site: null });
  const [societeDialog, setSocieteDialog] = useState({ open: false, societe: null });
  const [userDialog, setUserDialog] = useState({ open: false, user: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', item: null });
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Form states
  const [deptForm, setDeptForm] = useState({ name: '', societeId: '' });
  const [siteForm, setSiteForm] = useState({ name: '', city: '', country: 'Mali' });
  const [societeForm, setSocieteForm] = useState({ name: '', description: '' });
  const [userForm, setUserForm] = useState({ firstName: '', lastName: '', email: '', password: '', role: 'Viewer' });
  const [saving, setSaving] = useState(false);

  // Azure AD states
  const [azureConfig, setAzureConfig] = useState(null);
  const [azureForm, setAzureForm] = useState({ tenantId: '', clientId: '', clientSecret: '' });
  const [azureSyncing, setAzureSyncing] = useState(false);
  const [azureConfiguring, setAzureConfiguring] = useState(false);

  useEffect(() => {
    fetchData();
    fetchAzureConfig();
    fetchPlanLimits();
  }, []);

  const fetchData = async () => {
    try {
      const [deptRes, siteRes, societeRes, userRes] = await Promise.all([
        api.get('/departments'),
        api.get('/sites'),
        api.get('/societes'),
        api.get('/users')
      ]);
      setDepartments(deptRes.data.departments || deptRes.data || []);
      setSites(siteRes.data || []);
      setSocietes(societeRes.data || []);
      setUsers(userRes.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanLimits = async () => {
    try {
      // Récupérer le plan de l'entreprise et les limites
      const subResponse = await api.get('/billing/subscription');
      const subscription = subResponse.data;
      const isPro = subscription?.plan === 'pro';
      
      if (!isPro) {
        // Pour le plan gratuit, récupérer les limites depuis les paramètres système
        try {
          const limitsResponse = await api.get('/settings/plan-limits');
          setPlanLimits({
            maxUsers: limitsResponse.data.maxUsers || 1,
            maxEmployees: limitsResponse.data.maxEmployees || 10,
            isPro: false
          });
        } catch {
          // Valeurs par défaut si l'endpoint n'existe pas
          setPlanLimits({ maxUsers: 1, maxEmployees: 10, isPro: false });
        }
      } else {
        setPlanLimits({ maxUsers: 999, maxEmployees: 999, isPro: true });
      }
    } catch (error) {
      console.error('Error fetching plan limits:', error);
      // Assume free plan by default
      setPlanLimits({ maxUsers: 1, maxEmployees: 10, isPro: false });
    }
  };

  const fetchAzureConfig = async () => {
    try {
      const response = await api.get('/azure/config');
      setAzureConfig(response.data);
      if (response.data.configured) {
        setAzureForm({
          tenantId: response.data.tenantId || '',
          clientId: response.data.clientId || '',
          clientSecret: ''
        });
      }
    } catch (error) {
      console.error('Error fetching Azure config:', error);
    }
  };

  // Department handlers
  const openDeptDialog = (dept = null) => {
    setDeptForm({ name: dept?.name || '' });
    setDeptDialog({ open: true, dept });
  };

  const saveDepartment = async () => {
    if (!deptForm.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    setSaving(true);
    try {
      if (deptDialog.dept) {
        await api.put(`/departments/${deptDialog.dept.id}`, deptForm);
        toast.success('Département mis à jour');
      } else {
        await api.post('/departments', deptForm);
        toast.success('Département créé');
      }
      fetchData();
      setDeptDialog({ open: false, dept: null });
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Site handlers
  const openSiteDialog = (site = null) => {
    setSiteForm({ 
      name: site?.name || '', 
      city: site?.city || '', 
      country: site?.country || 'Mali' 
    });
    setSiteDialog({ open: true, site });
  };

  const saveSite = async () => {
    if (!siteForm.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    setSaving(true);
    try {
      if (siteDialog.site) {
        await api.put(`/sites/${siteDialog.site.id}`, siteForm);
        toast.success('Site mis à jour');
      } else {
        await api.post('/sites', siteForm);
        toast.success('Site créé');
      }
      fetchData();
      setSiteDialog({ open: false, site: null });
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Societe handlers
  const openSocieteDialog = (s = null) => {
    setSocieteForm({ 
      name: s?.name || '', 
      description: s?.description || ''
    });
    setSocieteDialog({ open: true, societe: s });
  };

  const saveSociete = async () => {
    if (!societeForm.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    setSaving(true);
    try {
      if (societeDialog.societe) {
        await api.put(`/societes/${societeDialog.societe.id}`, societeForm);
        toast.success('Société mise à jour');
      } else {
        await api.post('/societes', societeForm);
        toast.success('Société créée');
      }
      fetchData();
      setSocieteDialog({ open: false, societe: null });
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // User handlers
  const openUserDialog = (u = null) => {
    setUserForm({ 
      firstName: u?.firstName || '', 
      lastName: u?.lastName || '', 
      email: u?.email || '', 
      password: '',
      role: u?.role || 'Viewer'
    });
    setUserDialog({ open: true, user: u });
  };

  const saveUser = async () => {
    if (!userForm.firstName.trim() || !userForm.lastName.trim() || !userForm.email.trim()) {
      toast.error('Tous les champs sont requis');
      return;
    }
    if (!userDialog.user && !userForm.password) {
      toast.error('Le mot de passe est requis');
      return;
    }
    setSaving(true);
    try {
      if (userDialog.user) {
        await api.put(`/users/${userDialog.user.id}`, {
          firstName: userForm.firstName,
          lastName: userForm.lastName,
          role: userForm.role
        });
        toast.success('Utilisateur mis à jour');
      } else {
        await api.post('/users', userForm);
        toast.success('Utilisateur créé');
      }
      fetchData();
      setUserDialog({ open: false, user: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    const { type, item } = deleteDialog;
    try {
      if (type === 'department') {
        await api.delete(`/departments/${item.id}`);
        toast.success('Département supprimé');
      } else if (type === 'site') {
        await api.delete(`/sites/${item.id}`);
        toast.success('Site supprimé');
      } else if (type === 'societe') {
        await api.delete(`/societes/${item.id}`);
        toast.success('Société supprimée');
      } else if (type === 'user') {
        await api.delete(`/users/${item.id}`);
        toast.success('Utilisateur supprimé');
      }
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialog({ open: false, type: '', item: null });
    }
  };

  // Password reset handler
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setResettingPassword(true);
    try {
      await api.post('/auth/admin-reset-password', {
        userId: resetPasswordDialog.user.id,
        newPassword: newPassword
      });
      toast.success(`Mot de passe réinitialisé pour ${resetPasswordDialog.user.firstName} ${resetPasswordDialog.user.lastName}`);
      setResetPasswordDialog({ open: false, user: null });
      setNewPassword('');
      setShowNewPassword(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setResettingPassword(false);
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setShowNewPassword(true);
  };

  // Azure AD handlers
  const saveAzureConfig = async () => {
    if (!azureForm.tenantId || !azureForm.clientId || !azureForm.clientSecret) {
      toast.error('Tous les champs Azure AD sont requis');
      return;
    }
    setAzureConfiguring(true);
    try {
      await api.post('/azure/config', azureForm);
      toast.success('Configuration Azure AD sauvegardée');
      fetchAzureConfig();
      setAzureForm({ ...azureForm, clientSecret: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de configuration Azure AD');
    } finally {
      setAzureConfiguring(false);
    }
  };

  const deleteAzureConfig = async () => {
    try {
      await api.delete('/azure/config');
      toast.success('Configuration Azure AD supprimée');
      setAzureConfig({ configured: false });
      setAzureForm({ tenantId: '', clientId: '', clientSecret: '' });
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const syncAzureEmployees = async () => {
    setAzureSyncing(true);
    try {
      const response = await api.post('/azure/sync');
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.warning(response.data.message);
        if (response.data.errors?.length > 0) {
          response.data.errors.forEach(err => toast.error(err));
        }
      }
      fetchAzureConfig();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de synchronisation');
    } finally {
      setAzureSyncing(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Paramètres">
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Paramètres">
      <div className="space-y-6" data-testid="settings-page">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Paramètres
          </h1>
          <p className="text-slate-600">
            Gérez les départements, sites, utilisateurs et synchronisation Azure AD
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="departments" className="data-[state=active]:bg-white">
              <Building2 className="w-4 h-4 mr-2" />
              Départements
            </TabsTrigger>
            <TabsTrigger value="sites" className="data-[state=active]:bg-white">
              <MapPin className="w-4 h-4 mr-2" />
              Sites
            </TabsTrigger>
            <TabsTrigger value="societes" className="data-[state=active]:bg-white">
              <Briefcase className="w-4 h-4 mr-2" />
              Sociétés
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-white">
              <Users className="w-4 h-4 mr-2" />
              Utilisateurs
            </TabsTrigger>
            <TabsTrigger value="azure" className="data-[state=active]:bg-white">
              <Cloud className="w-4 h-4 mr-2" />
              Azure AD
            </TabsTrigger>
            <TabsTrigger value="employee-accounts" className="data-[state=active]:bg-white">
              <UserPlus className="w-4 h-4 mr-2" />
              Comptes employés
            </TabsTrigger>
            <TabsTrigger value="branding" className="data-[state=active]:bg-white">
              <Palette className="w-4 h-4 mr-2" />
              Personnalisation
            </TabsTrigger>
            <TabsTrigger value="production" className="data-[state=active]:bg-white">
              <Factory className="w-4 h-4 mr-2" />
              Production
            </TabsTrigger>
            <TabsTrigger value="email" className="data-[state=active]:bg-white">
              <Mail className="w-4 h-4 mr-2" />
              Email
            </TabsTrigger>
          </TabsList>

          {/* Email Tab */}
          <TabsContent value="email">
            <CompanyEmailSettings />
          </TabsContent>

          {/* Departments Tab */}
          <TabsContent value="departments">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Départements</CardTitle>
                  <CardDescription>Gérez la liste des départements</CardDescription>
                </div>
                <Button onClick={() => openDeptDialog()} className="bg-webtech-blue hover:bg-webtech-blue-hover text-white" data-testid="add-department-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {departments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center text-slate-500 py-8">
                          Aucun département
                        </TableCell>
                      </TableRow>
                    ) : (
                      departments.map((dept) => (
                        <TableRow key={dept.id}>
                          <TableCell className="font-medium">{dept.name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openDeptDialog(dept)}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setDeleteDialog({ open: true, type: 'department', item: dept })}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sites Tab */}
          <TabsContent value="sites">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Sites</CardTitle>
                  <CardDescription>Gérez la liste des sites/agences</CardDescription>
                </div>
                <Button onClick={() => openSiteDialog()} className="bg-webtech-blue hover:bg-webtech-blue-hover text-white" data-testid="add-site-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Ville</TableHead>
                      <TableHead>Pays</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sites.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                          Aucun site
                        </TableCell>
                      </TableRow>
                    ) : (
                      sites.map((site) => (
                        <TableRow key={site.id}>
                          <TableCell className="font-medium">{site.name}</TableCell>
                          <TableCell>{site.city || '-'}</TableCell>
                          <TableCell>{site.country || '-'}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openSiteDialog(site)}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setDeleteDialog({ open: true, type: 'site', item: site })}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Societes Tab */}
          <TabsContent value="societes">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5" />
                    Sociétés du Groupe
                  </CardTitle>
                  <CardDescription>
                    Gérez les différentes sociétés/filiales de votre groupe
                  </CardDescription>
                </div>
                <Button onClick={() => openSocieteDialog()} className="bg-webtech-blue hover:bg-webtech-blue-hover text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle société
                </Button>
              </CardHeader>
              <CardContent>
                {societes.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Briefcase className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Aucune société créée</p>
                    <p className="text-sm mt-1">Ajoutez vos premières sociétés/filiales</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nom</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {societes.map((societe) => (
                        <TableRow key={societe.id}>
                          <TableCell className="font-medium">{societe.name}</TableCell>
                          <TableCell className="text-slate-500">{societe.description || '-'}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => openSocieteDialog(societe)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="text-red-600"
                                onClick={() => setDeleteDialog({ open: true, type: 'societe', item: societe })}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Utilisateurs
                    <Badge className={users.length >= planLimits.maxUsers ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}>
                      {users.length} / {planLimits.maxUsers}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    {users.length >= planLimits.maxUsers ? (
                      <span className="text-amber-600">
                        Limite atteinte ({planLimits.maxUsers} utilisateurs). 
                        <a href="/billing/upgrade" className="underline ml-1">Augmenter votre quota</a>
                      </span>
                    ) : (
                      `Gérez les accès à l'application (${planLimits.maxUsers - users.length} places restantes)`
                    )}
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => openUserDialog()} 
                  className="bg-webtech-blue hover:bg-webtech-blue-hover text-white" 
                  data-testid="add-user-btn"
                  disabled={users.length >= planLimits.maxUsers}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </CardHeader>
              <CardContent>
                {/* Search + role filter */}
                <div className="mb-4 flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Rechercher par nom, email ou rôle…"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-9"
                      data-testid="users-search-input"
                    />
                  </div>
                  <select
                    value={userRoleFilter}
                    onChange={(e) => setUserRoleFilter(e.target.value)}
                    data-testid="users-role-filter"
                    className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-webtech-blue/20"
                  >
                    <option value="all">Tous les rôles</option>
                    <option value="Admin">Admin</option>
                    <option value="Manager">Manager</option>
                    <option value="Employé">Employé</option>
                    <option value="Viewer">Viewer</option>
                  </select>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Rôle</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const term = userSearch.trim().toLowerCase();
                      const filteredUsers = users.filter(u => {
                        if (userRoleFilter !== 'all' && u.role !== userRoleFilter) return false;
                        if (!term) return true;
                        return (
                          `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase().includes(term) ||
                          (u.email || '').toLowerCase().includes(term) ||
                          (u.role || '').toLowerCase().includes(term)
                        );
                      });
                      if (users.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                              Aucun utilisateur
                            </TableCell>
                          </TableRow>
                        );
                      }
                      if (filteredUsers.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                              Aucun résultat pour ces critères
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return filteredUsers.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium">{u.firstName} {u.lastName}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>{u.role}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openUserDialog(u)} title="Modifier">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              {u.id !== user?.id && (
                                <>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                    onClick={() => {
                                      setResetPasswordDialog({ open: true, user: u });
                                      setNewPassword('');
                                      setShowNewPassword(false);
                                    }}
                                    title="Réinitialiser le mot de passe"
                                  >
                                    <Key className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => setDeleteDialog({ open: true, type: 'user', item: u })}
                                    title="Supprimer"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Azure AD Tab */}
          <TabsContent value="azure">
            <div className="space-y-6">
              {/* Instructions */}
              <Card className="border-blue-200 bg-blue-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <Info className="w-5 h-5" />
                    Configuration Azure AD - Instructions
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-blue-900 space-y-4">
                  <p>
                    Pour synchroniser vos employés depuis Microsoft Azure Active Directory (Entra ID), 
                    vous devez créer une application dans Azure Portal.
                  </p>
                  <div className="space-y-2">
                    <p className="font-semibold">Étapes de configuration :</p>
                    <ol className="list-decimal list-inside space-y-2 ml-2">
                      <li>
                        Connectez-vous à <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline inline-flex items-center gap-1">
                          Azure Portal <ExternalLink className="w-3 h-3" />
                        </a>
                      </li>
                      <li>Allez dans <strong>Azure Active Directory</strong> → <strong>Inscriptions d'applications</strong></li>
                      <li>Cliquez sur <strong>Nouvelle inscription</strong> et nommez l'application (ex: "Webtech HR Sync")</li>
                      <li>Après création, notez l'<strong>ID d'application (client)</strong> et l'<strong>ID de répertoire (tenant)</strong></li>
                      <li>Dans <strong>Certificats et secrets</strong>, créez un <strong>Nouveau secret client</strong></li>
                      <li>
                        Dans <strong>Autorisations d'API</strong>, ajoutez :<br/>
                        <code className="bg-blue-100 px-1 rounded">Microsoft Graph → User.Read.All</code> (Application)
                      </li>
                      <li>Cliquez sur <strong>Accorder le consentement administrateur</strong></li>
                    </ol>
                  </div>
                </CardContent>
              </Card>

              {/* Configuration Form */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Cloud className="w-5 h-5 text-webtech-blue" />
                        Configuration Azure AD
                      </CardTitle>
                      <CardDescription>
                        Connectez votre annuaire Microsoft pour synchroniser les employés
                      </CardDescription>
                    </div>
                    {azureConfig?.configured && (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Configuré
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="tenantId">ID du tenant (Directory ID)</Label>
                      <Input
                        id="tenantId"
                        value={azureForm.tenantId}
                        onChange={(e) => setAzureForm({ ...azureForm, tenantId: e.target.value })}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        data-testid="azure-tenant-id"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="clientId">ID de l'application (Client ID)</Label>
                      <Input
                        id="clientId"
                        value={azureForm.clientId}
                        onChange={(e) => setAzureForm({ ...azureForm, clientId: e.target.value })}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        data-testid="azure-client-id"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="clientSecret">Secret client</Label>
                      <Input
                        id="clientSecret"
                        type="password"
                        value={azureForm.clientSecret}
                        onChange={(e) => setAzureForm({ ...azureForm, clientSecret: e.target.value })}
                        placeholder={azureConfig?.configured ? "••••••••••••••••" : "Entrez le secret client"}
                        data-testid="azure-client-secret"
                      />
                      {azureConfig?.configured && (
                        <p className="text-xs text-slate-500">
                          Laissez vide pour conserver le secret actuel, ou entrez un nouveau secret pour le mettre à jour.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-4 border-t">
                    <Button 
                      onClick={saveAzureConfig}
                      disabled={azureConfiguring}
                      className="bg-webtech-blue hover:bg-webtech-blue-hover text-white"
                      data-testid="save-azure-config-btn"
                    >
                      {azureConfiguring ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      {azureConfig?.configured ? 'Mettre à jour' : 'Enregistrer la configuration'}
                    </Button>
                    
                    {azureConfig?.configured && (
                      <Button 
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={deleteAzureConfig}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Sync Section */}
              {azureConfig?.configured && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 text-webtech-blue" />
                      Synchronisation des employés
                    </CardTitle>
                    <CardDescription>
                      Importez automatiquement les utilisateurs depuis votre annuaire Azure AD
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {azureConfig.lastSyncAt && (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600">
                          <strong>Dernière synchronisation :</strong>{' '}
                          {new Date(azureConfig.lastSyncAt).toLocaleString('fr-FR')}
                        </p>
                      </div>
                    )}
                    
                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                      <p className="text-sm text-amber-800">
                        <strong>Note :</strong> La synchronisation va importer tous les utilisateurs de votre annuaire Azure AD. 
                        Les employés existants (même email) seront mis à jour. Les nouveaux seront créés automatiquement.
                        Les départements seront également créés s'ils n'existent pas.
                      </p>
                    </div>

                    <Button 
                      onClick={syncAzureEmployees}
                      disabled={azureSyncing}
                      size="lg"
                      className="bg-webtech-blue hover:bg-webtech-blue-hover text-white"
                      data-testid="sync-azure-btn"
                    >
                      {azureSyncing ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Synchronisation en cours...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2" />
                          Lancer la synchronisation
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Employee Accounts Tab */}
          <TabsContent value="employee-accounts">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-webtech-blue" />
                      Gestion des comptes employés
                    </CardTitle>
                    <CardDescription>
                      Créez des comptes utilisateurs pour permettre aux employés d'accéder à leurs évaluations de compétences
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Pourquoi créer des comptes employés ?</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-700">
                        <li>Permettre aux employés de faire leur auto-évaluation de compétences</li>
                        <li>Accès à leur profil de compétences et analyses des écarts</li>
                        <li>Consultation de leurs plans de développement</li>
                        <li>Réception des notifications par email</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={() => navigate('/settings/employee-accounts')}
                  size="lg"
                  className="bg-webtech-blue hover:bg-webtech-blue-hover text-white"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  Gérer les comptes employés
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <BrandingSettings />
          </TabsContent>

          {/* Production Tab (Lignes + Shifts) */}
          <TabsContent value="production">
            <ProductionSettings />
          </TabsContent>
        </Tabs>

        {/* Department Dialog */}
        <Dialog open={deptDialog.open} onOpenChange={(open) => setDeptDialog({ open, dept: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{deptDialog.dept ? 'Modifier' : 'Nouveau'} département</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nom du département</Label>
                <Input
                  value={deptForm.name}
                  onChange={(e) => setDeptForm({ name: e.target.value })}
                  placeholder="Ex: Ressources Humaines"
                  data-testid="dept-name-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeptDialog({ open: false, dept: null })}>
                Annuler
              </Button>
              <Button onClick={saveDepartment} disabled={saving} className="bg-webtech-blue hover:bg-webtech-blue-hover text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Site Dialog */}
        <Dialog open={siteDialog.open} onOpenChange={(open) => setSiteDialog({ open, site: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{siteDialog.site ? 'Modifier' : 'Nouveau'} site</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nom du site</Label>
                <Input
                  value={siteForm.name}
                  onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
                  placeholder="Ex: Siège Bamako"
                  data-testid="site-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ville</Label>
                  <Input
                    value={siteForm.city}
                    onChange={(e) => setSiteForm({ ...siteForm, city: e.target.value })}
                    placeholder="Ex: Bamako"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pays</Label>
                  <Input
                    value={siteForm.country}
                    onChange={(e) => setSiteForm({ ...siteForm, country: e.target.value })}
                    placeholder="Ex: Mali"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSiteDialog({ open: false, site: null })}>
                Annuler
              </Button>
              <Button onClick={saveSite} disabled={saving} className="bg-webtech-blue hover:bg-webtech-blue-hover text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Societe Dialog */}
        <Dialog open={societeDialog.open} onOpenChange={(open) => setSocieteDialog({ open, societe: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{societeDialog.societe ? 'Modifier' : 'Nouvelle'} société</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nom de la société *</Label>
                <Input
                  value={societeForm.name}
                  onChange={(e) => setSocieteForm({ ...societeForm, name: e.target.value })}
                  placeholder="Ex: AMI Consulting, AMI Technologies..."
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <textarea
                  value={societeForm.description}
                  onChange={(e) => setSocieteForm({ ...societeForm, description: e.target.value })}
                  placeholder="Description de l'activité de la société..."
                  className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-webtech-blue focus:border-transparent resize-none"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSocieteDialog({ open: false, societe: null })}>
                Annuler
              </Button>
              <Button onClick={saveSociete} disabled={saving} className="bg-webtech-blue hover:bg-webtech-blue-hover text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Dialog */}
        <Dialog open={userDialog.open} onOpenChange={(open) => setUserDialog({ open, user: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{userDialog.user ? 'Modifier' : 'Nouvel'} utilisateur</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom</Label>
                  <Input
                    value={userForm.firstName}
                    onChange={(e) => setUserForm({ ...userForm, firstName: e.target.value })}
                    data-testid="user-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input
                    value={userForm.lastName}
                    onChange={(e) => setUserForm({ ...userForm, lastName: e.target.value })}
                    data-testid="user-lastname-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  disabled={!!userDialog.user}
                  data-testid="user-email-input"
                />
              </div>
              {!userDialog.user && (
                <div className="space-y-2">
                  <Label>Mot de passe</Label>
                  <Input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    data-testid="user-password-input"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select
                  value={userForm.role}
                  onValueChange={(value) => setUserForm({ ...userForm, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue placeholder="Sélectionner un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin RH</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Employé">Employé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUserDialog({ open: false, user: null })}>
                Annuler
              </Button>
              <Button onClick={saveUser} disabled={saving} className="bg-webtech-blue hover:bg-webtech-blue-hover text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordDialog.open} onOpenChange={(open) => {
          if (!open) {
            setResetPasswordDialog({ open: false, user: null });
            setNewPassword('');
            setShowNewPassword(false);
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-orange-500" />
                Réinitialiser le mot de passe
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-600">
                  Utilisateur : <span className="font-medium text-slate-900">
                    {resetPasswordDialog.user?.firstName} {resetPasswordDialog.user?.lastName}
                  </span>
                </p>
                <p className="text-sm text-slate-500">{resetPasswordDialog.user?.email}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Nouveau mot de passe</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Minimum 8 caractères"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={generateRandomPassword}
                    title="Générer un mot de passe aléatoire"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  L'utilisateur recevra un email de notification.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetPasswordDialog({ open: false, user: null })}>
                Annuler
              </Button>
              <Button 
                onClick={handleResetPassword} 
                disabled={resettingPassword || newPassword.length < 8}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {resettingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Réinitialisation...
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Réinitialiser
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
